import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowRight, ArrowLeft, AlertTriangle, CheckCircle, HelpCircle, FileText, Save, Cloud, LayoutDashboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useAutoSave } from '../hooks/useAutoSave';


// Custom styled select item for colored options
const ColoredSelectItem = ({ value, color, children }) => {
  const colorClasses = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
    default: 'text-black'
  };
  
  return (
    <SelectItem value={value} className={colorClasses[color] || colorClasses.default}>
      {children}
    </SelectItem>
  );
};

// Display component for selected value with color
const SelectedValueDisplay = ({ value, options }) => {
  const option = options.find(opt => opt.value === value);
  if (!option) return <span className="text-slate-500">Select an option</span>;
  
  const colorClasses = {
    green: 'text-green-600 font-medium',
    yellow: 'text-yellow-600 font-medium',
    red: 'text-red-600 font-medium',
    default: 'text-black'
  };
  
  return <span className={colorClasses[option.color] || colorClasses.default}>{option.label}</span>;
};

const DocumentationReadiness = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [subscription, setSubscription] = useState(null);
  
  const [formData, setFormData] = useState({
    policy_documents: '',
    sops_defined: '',
    annual_review: '',
    risk_register: '',
    isms_scope: '',
    internal_audit: '',
    training_records: '',
    supplier_documentation: '',
    incident_playbooks: '',
    management_review_minutes: ''
  });

  // Auto-save hook - saves every 60 seconds
  const { 
    isSaving, 
    lastSaved, 
    saveDraft, 
    deleteDraft,
    saveSuccess
  } = useAutoSave('documentation_readiness', orgId, formData, setFormData, token, 30000, !initialLoading, false);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(response.data);
      } catch (error) {
        console.log('Could not fetch subscription status');
      }
    };
    fetchSubscription();
  }, [token]);

  // Load existing data on mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [persistedResponse, draftResponse] = await Promise.all([
          axios.get(`${API_URL}/api/documentation-readiness/${orgId}`, { headers, params: { optional: true } }),
          axios.get(`${API_URL}/api/drafts/documentation_readiness`, { headers, params: { org_id: orgId } }).catch(() => ({ data: null }))
        ]);
        setFormData((current) => ({ ...current, ...(persistedResponse.data || {}), ...(draftResponse.data?.draft?.data || {}) }));
      } catch (error) {
        console.log('No existing documentation readiness data');
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingData();
  }, [orgId, token]);

  // Track unsaved changes
  const updateFormData = useCallback((newData) => {
    setFormData(newData);
    setHasUnsavedChanges(true);
  }, []);

  // Browser tab close warning - always show on assessment pages
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const message = 'Data you entered is not saved. Are you sure you want to close?';
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Standard Yes/No/Unaware options
  const standardOptions = [
    { value: 'yes', label: 'Yes', color: 'green' },
    { value: 'no', label: 'No', color: 'red' },
    { value: 'unaware', label: 'Unaware', color: 'yellow' }
  ];

  // Question definitions
  const questions = [
    {
      id: 'policy_documents',
      number: 1,
      question: 'Policy documents are prepared, maintained, updated and audited regularly?',
      options: standardOptions
    },
    {
      id: 'sops_defined',
      number: 2,
      question: 'SOPs are defined, executed and followed across organization?',
      options: standardOptions
    },
    {
      id: 'annual_review',
      number: 3,
      question: 'Annual review of policies and critical documents is being followed in the system?',
      options: standardOptions
    },
    {
      id: 'risk_register',
      number: 4,
      question: 'Risk register is created, maintained and audited regularly?',
      options: standardOptions
    },
    {
      id: 'isms_scope',
      number: 5,
      question: 'ISMS scope is defined, documented, and approved in the organization?',
      options: standardOptions
    },
    {
      id: 'internal_audit',
      number: 6,
      question: 'Internal audit is being conducted regularly?',
      options: standardOptions
    },
    {
      id: 'training_records',
      number: 7,
      question: 'Training records are created and documented timely?',
      options: standardOptions
    },
    {
      id: 'supplier_documentation',
      number: 8,
      question: 'Supplier documentation is defined, maintained and optimized?',
      options: standardOptions
    },
    {
      id: 'incident_playbooks',
      number: 9,
      question: 'Incident playbooks are created, circulated and followed in the company?',
      options: standardOptions
    },
    {
      id: 'management_review_minutes',
      number: 10,
      question: 'Minutes of management reviews is maintained, documented, audited and approved regularly?',
      options: standardOptions
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all questions are answered
    for (const question of questions) {
      if (!formData[question.id]) {
        toast.error(`Please answer question ${question.number}: "${question.question}"`);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Save documentation readiness data
      await axios.post(
        `${API_URL}/api/documentation-readiness`,
        { 
          org_id: orgId,
          ...formData 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasUnsavedChanges(false);
      await deleteDraft(); // Clean up draft after successful save
      toast.success('Documentation readiness assessment saved!');
      
      // For Pro Pack users, navigate to Controls Assessment (93 controls)
      // For Standard Pack users, navigate directly to Assessment Summary
      if (subscription?.plan === 'pro') {
        navigate(`/controls-assessment/${orgId}`);
      } else {
        navigate(`/assessment-summary/${orgId}`);
      }
    } catch (error) {
      toast.error('Failed to save documentation readiness assessment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/technical-controls/${orgId}`);
  };

  // Get icon based on selected value color
  const getStatusIcon = (questionId) => {
    const value = formData[questionId];
    if (!value) return null;
    
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.value === value);
    
    if (!option) return null;
    
    switch (option.color) {
      case 'green':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'yellow':
        return <HelpCircle className="h-5 w-5 text-yellow-500" />;
      case 'red':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="font-body text-lg text-slate-600">Loading assessment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-12">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Dashboard Button */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <LayoutDashboard className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-8 w-8 text-slate-900" />
              <h1 className="font-heading font-bold text-4xl text-black">
                Documentation Readiness
              </h1>
            </div>
            <p className="font-body text-lg text-slate-700">
              Assess your organization&apos;s documentation and policy management practices
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-600">Step 4 of 5</span>
              <span className="font-mono text-xs text-slate-600">Documentation Assessment</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-slate-900 w-4/5 progress-bar"></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
            
            {/* Questions */}
            {questions.map((question) => (
              <div key={question.id} className="pb-4 border-b border-slate-100 last:border-b-0">
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  {question.number}. {question.question} <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={formData[question.id]}
                    onValueChange={(value) => updateFormData({ ...formData, [question.id]: value })}
                  >
                    <SelectTrigger data-testid={`${question.id}-select`} className="text-black flex-1">
                      {formData[question.id] ? (
                        <SelectedValueDisplay value={formData[question.id]} options={question.options} />
                      ) : (
                        <span className="text-slate-500">Select an option</span>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {question.options.map((option) => (
                        <ColoredSelectItem key={option.value} value={option.value} color={option.color}>
                          {option.label}
                        </ColoredSelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="w-6">
                    {getStatusIcon(question.id)}
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Buttons */}
            <div className="pt-6 border-t border-slate-200">
              {/* Save Draft Button with Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm">
                  {isSaving ? (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </span>
                  ) : saveSuccess ? (
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <Cloud className="h-4 w-4" />
                      Draft saved successfully
                    </span>
                  ) : lastSaved ? (
                    <span className="text-slate-500">
                      <Cloud className="h-4 w-4 inline mr-1" />
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={saveDraft}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Draft
                </Button>
              </div>
              
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 py-6 text-base font-semibold"
                  data-testid="back-button"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 py-6 text-base font-semibold"
                  disabled={loading}
                  data-testid="next-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default DocumentationReadiness;
